import { StorageFields } from 'application/constants/localStorage';
import { AuthLocalStorage } from 'application/sessionStorage/auth';
import { store } from 'application/store';
import { clearStore } from 'application/store/reducers/Users/UsersSlice';
import { PathNames } from 'application/routes';

const logoutChannel = new BroadcastChannel('logout');
const loginChannel = new BroadcastChannel('login');
/**
 * Logs out the user by broadcasting a 'Logout' message to all tabs, clearing the store, and redirecting to the login page.
 */
export const logout = () => {
  logoutChannel.postMessage('Logout');
  window.location.href = window.location.origin + PathNames.login;
  store?.dispatch(clearStore());
};

/**
 * Logs in the user by broadcasting a 'Login' message to all tabs and redirecting to the home page.
 */
export const login = () => {
  loginChannel.postMessage('Login');
  window.location.href = window.location.origin;
};

/**
 * Listens for broadcasted 'Logout' messages from other tabs and logs out the current tab if one is received.
 */
export const logoutAllTabs = () => {
  logoutChannel.onmessage = () => {
    logout();
    logoutChannel.close();
  };
};

/**
 * Listens for broadcasted 'Login' messages from other tabs and logs in the current tab if one is received.
 */
export const loginAllTabs = () => {
  loginChannel.onmessage = () => {
    login();
    loginChannel.close();
  };
};

/**
 * Provides methods for managing authentication data in local storage.
 */
export const authService = {
  /**
   * Saves an access token to local storage.
   *
   * @param jwt - The JWT access token to save.
   */
  saveAccessToken(jwt: string) {
    localStorage.setItem(StorageFields.fbToken, jwt);
  },

  /**
   * Retrieves the access token from local storage.
   *
   * @returns The saved access token, or null if it does not exist.
   */
  getAccessToken() {
    return localStorage.getItem(StorageFields.fbToken);
  },

  /**
   * Checks whether the user is authenticated by verifying that an access token exists in local storage.
   *
   * @returns True if the user is authenticated; false otherwise.
   */
  checkAuth() {
    return !!localStorage.getItem(AuthLocalStorage.accessToken);
  },

  /**
   * Removes the access token from local storage.
   */
  removeToken() {
    localStorage.removeItem(AuthLocalStorage.accessToken);
  },

  /**
   * Saves the user's email address to local storage.
   *
   * @param email - The user's email address to save.
   */
  saveEmail(email: string) {
    localStorage.setItem(StorageFields.email, email);
  },

  /**
   * Retrieves the user's email address from local storage.
   *
   * @returns The saved email address, or null if it does not exist.
   */
  getEmail() {
    return localStorage.getItem(StorageFields.email);
  },

  /**
   * Saves a verification code to local storage.
   *
   * @param code - The verification code to save.
   */
  saveCode(code: number) {
    localStorage.setItem(StorageFields.verificationCode, String(code));
  },

  /**
   * Retrieves the saved verification code from local storage.
   *
   * @returns The saved verification code, or null if it does not exist.
   */
  getEmailVerifiationCode() {
    return localStorage.getItem(StorageFields.verificationCode);
  },

  /**
   * Saves a timer value to local storage.
   *
   * @param timer - The timer value to save.
   */
  saveTimer(timer: string) {
    localStorage.setItem(StorageFields.timer, timer);
  },

  /**
   * Retrieves the saved timer value from local storage.
   *
   * @returns The saved timer value, or null if it does not exist.
   */
  getTimer() {
    return localStorage.getItem(StorageFields.timer);
  },

  /**
   * Removes the timer value from local storage.
   */
  removeTimer() {
    localStorage.removeItem(StorageFields.timer);
  },

  /**
   * Clears all data stored in local and session storage.
   */
  cleanStorage() {
    localStorage.clear();
    sessionStorage.clear();
  },

  /**
   * Removes the access token from local storage to erase session data.
   */
  eraseSessionData() {
    this.removeToken();
  },
};
